import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'

export interface QRCodeStatus {
  total_trolleys: number
  trolleys_with_qr: number
  trolleys_without_qr: number
  needs_regeneration: boolean
  last_generated_at: string
  process_id: Nullable<string>
  [key: string]: unknown
}

interface UseGetQRCodeStatusReturn {
  status: Nullable<QRCodeStatus>
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useGetQRCodeStatus(): UseGetQRCodeStatusReturn {
  const [status, setStatus] = useState<Nullable<QRCodeStatus>>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const fetchStatus = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get<QRCodeStatus>('/qr-codes/status')
      setStatus(response.data)
    } catch (err) {
      setError(handleErrorRef.current(err))
      setStatus(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  return {
    status,
    isLoading,
    error,
    refetch: fetchStatus,
  }
}
